import axios, { type CancelTokenSource } from 'axios'
import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { ContactsApi } from 'entities/Contacts'
import type { ISkippedReason } from 'entities/Contacts/api/types'

type IReasonsType = 'skipped' | 'duplicate'

const defaultPagination = {
  page: 1,
  limit: 10,
}

export class ImportReasonsStore {
  private _data: ISkippedReason[] = []
  private _total = 0
  private _paginationData = defaultPagination
  private _loading = false
  private _contactImportId: number | null = null
  private _type: IReasonsType | null = null
  private _cancelTokenSource: CancelTokenSource | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get data() {
    return this._data
  }

  get total() {
    return this._total
  }

  get paginationData() {
    return this._paginationData
  }

  get loading() {
    return this._loading
  }

  private _cancelPendingRequest = () => {
    if (this._cancelTokenSource) {
      this._cancelTokenSource.cancel()
      this._cancelTokenSource = null
    }
  }

  load = async (contactImportId: number, type: IReasonsType) => {
    this._cancelPendingRequest()

    this._contactImportId = contactImportId
    this._type = type
    this._cancelTokenSource = axios.CancelToken.source()
    this._loading = true

    try {
      const { data } =
        type === 'skipped'
          ? await ContactsApi.getImportSkippedReasons(contactImportId, this._paginationData, {
              cancelToken: this._cancelTokenSource.token,
            })
          : await ContactsApi.getImportDuplicateReasons(contactImportId, this._paginationData, {
              cancelToken: this._cancelTokenSource.token,
            })

      runInAction(() => {
        this._data = data.data
        this._total = data.meta.total
        this._loading = false
      })
    } catch (e) {
      if (axios.isCancel(e)) return
      logger.error(e)
      runInAction(() => {
        this._loading = false
      })
    }
  }

  changePagination = (page: number, limit: number) => {
    this._paginationData = { page, limit }

    if (this._contactImportId !== null && this._type !== null) {
      this.load(this._contactImportId, this._type)
    }
  }

  reset = () => {
    this._cancelPendingRequest()
    this._data = []
    this._total = 0
    this._paginationData = defaultPagination
    this._contactImportId = null
    this._type = null
    this._loading = false
  }
}
